import React, { useState, useEffect, useContext, useMemo, useCallback } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Typography, useTheme, TextField, MenuItem, InputAdornment, CircularProgress } from '@mui/material';
import { MainHeader, SuccessIcon } from '@eposnow/ui-core';
import { CalendarIcon } from '@mui/x-date-pickers';
import { DatasetType } from '@mui/x-charts/internals';
import { AnalyticsOutlined, FileDownloadOutlined, FeedbackOutlined, ViewQuiltOutlined } from '@mui/icons-material';

import FeedbackBanner from '../../../components/feedback/FeedbackBanner';
import FeedbackDialog from '../../../components/feedback/FeedbackDialog';
import ResultFeedbackDialog from '../../../components/feedback/ResultFeedbackDialog';

import { useCompanyConfig } from '../../../api/companyConfig';
import { Bucket, GetIndicatorsRequest, SalesInsightsCompareAggregateResponse, SalesInsightsResponse, useSalesInsights, useSalesInsightsAggregated } from '../../../api/indicators';

import { dateRange, formatDateParam, formatDateTimeParam } from '../../../helpers/dateUtils';

import LocationSelector from '../components/LocationSelector';
import NumericChart from '../components/charts/numericChart/NumericChart';
import LineBarChart from '../components/charts/lineBarChart/LineBarChart';

import { DashboardFiltersContext, defaultFiltersDummy } from '../components/filters/DashboardFiltersContext';
import LinkTile from './components/LinkTile';
import { DashboardFilters } from '../../../models/dashboard';
import { useBannerUserActions } from '../../../api/promotionalBanner';
import { useDashboardAccess } from '../../../api/useDashboardAccess';
import Loading from '../../../components/Loading';
import { SettingsContext } from '../../../context/SettingsContext';

const periods = [
    {
        value: "today",
        translationKey: 'components.datePicker.today',
    },
    {
        value: "yesterday",
        translationKey: 'components.datePicker.yesterday',
    },
    {
        value: "thisWeek",
        translationKey: 'components.datePicker.thisWeek',
    },
    {
        value: "lastWeek",
        translationKey: 'components.datePicker.lastWeek',
    },
    {
        value: "thisMonth",
        translationKey: 'components.datePicker.thisMonth',
    },
    {
        value: "lastMonth",
        translationKey: 'components.datePicker.lastMonth',
    },
];

interface LinkTile {
    title: string;
    description: string;
    icon: React.ReactNode;
    linkText: string;
    onClick: () => void;
}

const DashboardLandingContent = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const navigate = useNavigate();
    const { hasDashboardAccess, isLoading: isLoadingDashboardAccess } = useDashboardAccess();
    const urlParams = new URLSearchParams(window.location.search);
    const returnToBackoffice = urlParams.get('backoffice');
    const { calendarPreferences } = useContext(SettingsContext);
    
    const redirectUrl = returnToBackoffice
        ? `${process.env.REACT_APP_LINK_BACK_OFFICE}Pages/Reporting/Dashboard.aspx`
        : '/transactions';

    const { dateRangeConfig } = useContext(DashboardFiltersContext);
    const { data: companyConfig } = useCompanyConfig('default');
    const [data, setData] = useState<DatasetType>([]);
    const [selectedMetrics, setSelectedMetrics] = useState<string[]>(['netSalesExcTax']);
    const [bannerOpen, setBannerOpen] = useState(true);
    const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);
    const [feedbackSuccessOpen, setFeedbackSuccessOpen] = useState(false);

    const [filters, setFilters] = useState<DashboardFilters>({
        ...defaultFiltersDummy,
        periodFrom: dateRange('today', undefined, undefined, undefined, dateRangeConfig).startDate,
        periodTo: dateRange('today', undefined, undefined, undefined, dateRangeConfig).endDate
    });
    const noData = !(filters.locations.length || filters.locationArea);
    
    const params = useMemo<GetIndicatorsRequest>(() => {
        const range = dateRange(filters.period, undefined, undefined, undefined, dateRangeConfig);
        console.log(range);
        return {
            fields: 'totalTax,netSales,totalSales,totalDiscount,totalServiceCharge,transactionVolume',

            startDateTime: formatDateTimeParam(range.startDate),
            startDate: formatDateParam(range.startDate),

            endDateTime: formatDateTimeParam(range.endDate),
            endDate: formatDateParam(range.endDate),

            locations: filters.locations.join(","),

            locationArea: filters.locationArea?.id,
            bucket: filters.bucket || 'day'
        };
    }, [filters, dateRangeConfig]);

    const updateFiltersLocation = useCallback((locations: string[]) => {
        setFilters(
            (prevFilters: DashboardFilters) =>
            ({
                ...prevFilters,
                locations,
            } as DashboardFilters)
        );
    }, [setFilters]);

    const handlePeriodChange = useCallback((newPeriod: string) => {
        setFilters((prevFilters: DashboardFilters) => ({
            ...prevFilters,
            period: newPeriod,
        }));
    }, []);

    const handleBucketChange = useCallback((newBucket: Bucket) => {
        setFilters((prevFilters: DashboardFilters) => ({
            ...prevFilters,
            bucket: newBucket,
        }));
    }, []);

    useEffect(() => {
        if (!companyConfig?.locations) {
            return;
        }
        updateFiltersLocation(companyConfig.locations.map((location) => location.id))
    }, [companyConfig, setFilters, updateFiltersLocation]);

    const metrics = useMemo(
        () => [
            {
                key: 'netSalesIncTax',
                field: 'netSales',
                label: t('screens.dashboarding.charts.netSalesIncludingTax'),
                color: theme.palette.primary.main,
            },
            {
                key: 'netSalesExcTax',
                field: 'netSales',
                label: t('screens.dashboarding.charts.netSalesExcludingTax'),
                color: theme.palette.success.main,
            },
            {
                key: 'totalSalesIncTax',
                field: 'totalSales',
                label: t('screens.dashboarding.charts.salesIncludingTax'),
                color: theme.palette.warning.main,
            },
            {
                key: 'totalSalesExcTax',
                field: 'totalSales',
                label: t('screens.dashboarding.charts.salesExcludingTax'),
                color: theme.palette.error.main,
            },
            {
                key: 'totalTax',
                field: 'totalTax',
                label: t('screens.dashboarding.charts.totalTax'),
                color: theme.palette.info.main,
            },
            {
                key: 'totalServiceCharge',
                field: 'totalServiceCharge',
                label: t('screens.dashboarding.charts.serviceCharge'),
                color: theme.palette.amber.main,
            },
            {
                key: 'totalCustomerDiscount',
                field: 'totalCustomerDiscount',
                label: t('screens.dashboarding.charts.customerDiscount'),
                color: theme.palette.fucsia.main,
            },
            {
                key: 'totalItemDiscount',
                field: 'totalItemDiscount',
                label: t('screens.dashboarding.charts.totalItemDiscounts'),
                color: theme.palette.cyan.main,
            },
            {
                key: 'totalBasketDiscount',
                field: 'totalBasketDiscount',
                label: t('screens.dashboarding.charts.definitions.BasketDiscount'),
                color: theme.palette.teal.main,
            },
            {
                key: 'totalAmountPaidByPoints',
                field: 'totalAmountPaidByPoints',
                label: t('screens.dashboarding.charts.paidByPoints'),
                color: theme.palette.lime.main,
            },
            {
                key: 'costPriceExcTax',
                field: 'costPrice',
                label: t('screens.dashboarding.charts.costPriceExcludingTax'),
                color: theme.palette.deepOrange.main,
            },
        ],
        [t, theme]
    );

    const {
        data: salesInsights,
        isLoading,
        isError,
    } = useSalesInsights<SalesInsightsResponse>({
            startDateTime: params.startDateTime,
            endDateTime: params.endDateTime,
            fields: 'totalTax,netSales,totalSales,totalDiscount,totalServiceCharge,transactionVolume,netMargin',
            locations: filters?.locations?.length > 0 ? filters?.locations.join(',') : undefined,
        }
        , 'sales-insights', 
        {enabled: !noData})
    ;

    const {
        data: chartDataResponse,
        isLoading: isChartLoading,
        isError: isChartError,
    } = useSalesInsightsAggregated<SalesInsightsCompareAggregateResponse>(
        {
            startDate: params.startDate,
            endDate: params.endDate,
            bucket: params.bucket,
            fields: ['transactionVolume', ...metrics.filter(metric => selectedMetrics.includes(metric.key)).map(metric => metric.field)].join(','),
            businessStartHour: calendarPreferences.daysStart,
            locations: filters?.locations?.length > 0 ? filters?.locations.join(',') : undefined,
        },
        'multiMetricChart'
    );

    useEffect(() => {
        if (chartDataResponse?.salesInsightsAggregate) {
            const formattedData = chartDataResponse.salesInsightsAggregate.map((item) => ({
                ...item.salesInsights,
                dateTime: new Date(item.dateTime),
            }));
            setData(formattedData);
        } else {
            setData([]);
        }
    }, [chartDataResponse]);

    let overallPercentage: number | undefined;
    let taxRatePercentage: number | undefined;

    if (salesInsights?.salesInsights?.totalTax && salesInsights?.salesInsights?.totalSalesIncTax) {
        taxRatePercentage = salesInsights.salesInsights.totalTax / salesInsights.salesInsights.totalSalesIncTax;
    }

    if (salesInsights?.salesInsights?.totalTax && salesInsights?.salesInsights?.totalSalesIncTax) {
        overallPercentage = salesInsights.salesInsights.totalTax / salesInsights.salesInsights.totalSalesIncTax;
    }

    const currency = salesInsights?.currency || 'GBP';

    let discountPercentage: number | undefined;
    if (salesInsights?.salesInsights?.totalServiceCharge && salesInsights?.salesInsights?.totalDiscount
        && salesInsights?.salesInsights?.totalDiscount !== 0
    ) {
        discountPercentage = (salesInsights.salesInsights.totalServiceCharge / salesInsights.salesInsights.totalDiscount);
    }

    const linkTiles: LinkTile[] = [
        {
            title: t('screens.dashboarding.landing.reportingHub.businessOverviewName'),
            description: t('screens.dashboarding.landing.reportingHub.businessOverviewDescription'),
            icon: <ViewQuiltOutlined sx={{ padding: '4px', color: '#595959' }} />,
            linkText: t('screens.dashboarding.landing.reportingHub.businessOverviewButton'),
            onClick: () => navigate('/dashboards'),
        },
        {
            title: t('screens.dashboarding.landing.reportingHub.transactionReportName'),
            description: t('screens.dashboarding.landing.reportingHub.transactionReportDescription'),
            icon: <AnalyticsOutlined sx={{ padding: '4px', color: '#595959' }} />,
            linkText: t('screens.dashboarding.landing.reportingHub.transactionReportButton'),
            onClick: () => navigate('/transactions'),
        },
        {
            title: t('screens.dashboarding.landing.reportingHub.dataDownloaderName'),
            description: t('screens.dashboarding.landing.reportingHub.dataDownloaderDescription'),
            icon: <FileDownloadOutlined sx={{ padding: '4px', color: '#595959' }} />,
            linkText: t('screens.dashboarding.landing.reportingHub.dataDownloaderButton'),
            onClick: () => navigate('/reports-hub'),
        },
    ];

    if (isLoadingDashboardAccess) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100vh',
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    if (isLoadingDashboardAccess) {
        return <Loading />;
    }

    if (!hasDashboardAccess) {
        if (returnToBackoffice) {
            window.location.replace(redirectUrl);
            return null;
        }

        return <Navigate to="/transactions" replace />;
    }

    return (
        <>
            {bannerOpen && (
                <FeedbackBanner
                    text={t('actions.feedbackDashboard')}
                    leftButtonText={t('actions.gotIt')}
                    rightButtonText={t('actions.feedback')}
                    iconElement={<FeedbackOutlined sx={{ paddingRight: '5px' }} />}
                    onLeftAction={() => setBannerOpen(false)}
                    onRightAction={() => setFeedbackDialogOpen(true)}
                />
            )}
            <FeedbackDialog
                dialogOpen={feedbackDialogOpen}
                description={t('actions.feedbackDialogDescriptionDashboardLanding')}
                featureKey="dashboard-landing"
                onClose={() => setFeedbackDialogOpen(false)}
                onSuccess={() => setFeedbackSuccessOpen(true)}
                inputText={t('actions.feedbackInputDashboard')}
            />
            <ResultFeedbackDialog
                successDialogOpen={feedbackSuccessOpen}
                onClose={() => setFeedbackSuccessOpen(false)}
                icon={<SuccessIcon />}
                title={t('actions.feedbackSent')}
                description={t('actions.successFeedbackDescription')}
                rightButtonText={t('actions.gotIt')}
                onRightAction={() => setFeedbackSuccessOpen(false)}
            />

            <Box
                sx={{
                    marginLeft: 0,
                    marginBottom: { xs: 1, sm: 2 },
                    paddingX: { xs: 2, sm: 0 },
                    height: 'auto',
                    paddingBottom: '70px',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                        <Box
                            sx={{
                                marginBottom: '8px',
                                marginLeft: '12px',
                            }}
                        >
                            <MainHeader title={t('screens.dashboarding.landing.title')} theme={theme} />
                        </Box>

                        <Box sx={{ marginLeft: '12px' }}>
                            <Typography sx={{ marginBottom: '24px' }} variant="body1">
                                {t('screens.dashboarding.landing.description')}
                            </Typography>
                        </Box>
                    </Box>

                    <Box
                        sx={{
                            display: 'flex',
                            gap: 2,
                        }}
                    >
                        <TextField
                            size="small"
                            select
                            value={filters.period}
                            onChange={(e) => {
                                handlePeriodChange(e.target.value);
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <CalendarIcon fontSize="small" />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{
                                width: 'fit-content',
                                backgroundColor: theme.palette.background.paper,
                                borderRadius: '4px',
                            }}
                        >
                            {periods.map((periodItem) => (
                                <MenuItem key={periodItem.value} value={periodItem.value}>
                                    {t(periodItem.translationKey)}
                                </MenuItem>
                            ))}
                        </TextField>

                        {companyConfig?.locations && <LocationSelector locations={companyConfig.locations} locationAreas={companyConfig.locationAreas || []} selectedLocations={filters.locations} onLocationChange={updateFiltersLocation} />}
                    </Box>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'row', marginBottom: '32px' }}>
                    <NumericChart
                        title={t('screens.dashboarding.landing.cards.sales')}
                        subtitle={t('screens.dashboarding.landing.cards.includingTax')}
                        alignment="double"
                        mainNumber={salesInsights?.salesInsights?.totalSalesIncTax}
                        mainCurrency={currency}
                        forceNoData={noData}
                        numbers={[
                            {
                                number: salesInsights?.salesInsights?.totalTax,
                                description: t('screens.dashboarding.landing.cards.totalTax'),
                                currency,
                            },
                            {
                                number: overallPercentage,
                                description: t('screens.dashboarding.landing.cards.overall'),
                                currency: undefined,
                                showAsPercentage: true,
                            },
                        ]}
                        isError={isError}
                        isLoading={isLoading}
                        linkChart={`${process.env.REACT_APP_LINK_BACK_OFFICE}Pages/Reporting/SalesBreakdown.aspx`}
                        buttonText={t('screens.dashboarding.landing.cards.salesOverview')}
                    />

                    <NumericChart
                        title={t('screens.dashboarding.charts.netMargin')}
                        subtitle={t('screens.dashboarding.charts.excludingTax')}
                        alignment="single"
                        mainNumber={salesInsights?.salesInsights?.netMargin}
                        mainCurrency={currency}
                        forceNoData={noData}
                        isError={isError}
                        isLoading={isLoading}
                        linkChart={`${process.env.REACT_APP_LINK_BACK_OFFICE}Pages/Reporting/SalesBreakdown.aspx`}
                        buttonText={t('screens.dashboarding.charts.salesOverview')}
                    />
                    <NumericChart
                        title={t("screens.dashboarding.charts.discounts")}
                        subtitle={t("screens.dashboarding.charts.includingTax")}
                        alignment="double"
                        mainNumber={salesInsights?.salesInsights.totalDiscount}
                        mainCurrency={salesInsights?.currency}
                        forceNoData={noData}
                        numbers={[
                            {
                                number: salesInsights?.salesInsights.totalServiceCharge,
                                description: t("screens.dashboarding.charts.serviceCharge"),
                                currency: salesInsights?.currency
                            },
                            {
                                number: discountPercentage,
                                description: t("screens.dashboarding.charts.overall"),
                                currency: salesInsights?.currency,
                                showAsPercentage: true
                            }
                        ]}
                        isError={isError}
                        isLoading={isLoading}
                        linkChart={`${process.env.REACT_APP_LINK_BACK_OFFICE}Pages/Reporting/ServiceCharges.aspx`}
                        buttonText={t("screens.dashboarding.charts.serviceCharge")}

                    />
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', marginBottom: '32px' }}>
                    <LineBarChart
                        isLoading={isChartLoading}
                        isError={isChartError}
                        leftAxisLabel={t('screens.dashboarding.charts.amount')}
                        rightAxisLabel={t('screens.dashboarding.charts.volume')}
                        barLabel={t('screens.dashboarding.charts.volumeOfTransactions')}
                        barDataKey="transactionVolume"
                        availableMetrics={metrics}
                        defaultSelectedMetrics={['netSalesExcTax']}
                        maxSelectedMetrics={3}
                        data={data}
                        height={600}
                        forceNoData={noData}
                        hideFiltersButton
                        bucket={filters.bucket || 'day'}
                        selectedMetrics={selectedMetrics}
                        onSelectedMetricsChange={setSelectedMetrics}
                        setBucket={handleBucketChange}
                    />
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px', marginX: '8px' }}>
                    <Box>
                        <Typography variant="h2" sx={{fontSize: '24px', lineHeight: '30px'}}>
                            {t('screens.dashboarding.landing.reportingHub.title')}
                        </Typography>
                        <Typography variant="body1">
                            {t('screens.dashboarding.landing.reportingHub.description')}
                        </Typography>
                    </Box>

                    <Box
                        sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '16px',
                            justifyContent: 'space-between',
                            '> *': {
                                flex: '1 1 calc(30% - 16px)',
                                minWidth: '250px',
                            },
                        }}
                    >
                        {linkTiles.map((tile) => (
                            <LinkTile
                                key={tile.title}
                                title={tile.title}
                                description={tile.description}
                                icon={tile.icon}
                                linkText={tile.linkText}
                                onClick={tile.onClick}
                            />
                        ))}
                    </Box>
                </Box>
            </Box>
        </>
    );
};

export default DashboardLandingContent;
